import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import type { ImportPlan } from '../lib/types';
import { exactCalibration } from '../geo/calibration';
import { toast, useCurrentMap, useStore } from '../store/store';

interface UnityExport {
  schema: string;
  schemaVersion: number;
  world: { worldId: string };
  render?: {
    widthPx: number;
    heightPx: number;
    worldRect: { minX: number; minZ: number; maxX: number; maxZ: number };
  };
  heightmap?: { minX: number; maxZ: number; step: number; width: number; height: number };
  exportedAt?: string;
  git?: { commit?: string; dirty?: boolean };
}

export function ImportAdmin() {
  const { t } = useTranslation();
  const map = useCurrentMap();
  const baseLayers = useStore((s) => s.baseLayers);
  const user = useStore((s) => s.user);
  const [payload, setPayload] = useState<UnityExport | null>(null);
  const [render, setRender] = useState<File | null>(null);
  const [heights, setHeights] = useState<File | null>(null);
  const [fastForward, setFastForward] = useState(true);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [busy, setBusy] = useState(false);

  const readJson = async (file: File | undefined) => {
    setPlan(null);
    if (!file) return setPayload(null);
    try {
      setPayload(JSON.parse(await file.text()));
    } catch {
      toast(t('admin.import.badJson'), 'error');
      setPayload(null);
    }
  };

  const run = async (dryRun: boolean) => {
    if (!payload || !map) return;
    setBusy(true);
    try {
      const q = `?dryRun=${dryRun ? 1 : 0}&fastForward=${fastForward ? 1 : 0}`;
      const res = await pb.send<ImportPlan>(`/api/mapeditor/maps/${map.slug}/import-unity${q}`, {
        method: 'POST',
        body: payload,
      });
      setPlan(res);
      if (!dryRun) {
        await uploadExtras();
        toast(t('admin.import.done'));
      }
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  };

  /** The top-down render becomes (or replaces) the "Unity render" base layer; heights go on the map. */
  const uploadExtras = async () => {
    if (!map || !payload) return;
    if (render && payload.render) {
      const r = payload.render;
      const fd = new FormData();
      fd.append('image', render);
      fd.append('widthPx', String(r.widthPx));
      fd.append('heightPx', String(r.heightPx));
      fd.append('calibration', JSON.stringify(exactCalibration(r.worldRect, r.widthPx, r.heightPx)));
      const existing = baseLayers.find((l) => l.source === 'unity_render');
      if (existing) {
        await pb.collection('base_layers').update(existing.id, fd);
      } else {
        fd.append('map', map.id);
        fd.append('name', t('admin.import.renderLayerName'));
        fd.append('kind', 'image');
        fd.append('source', 'unity_render');
        fd.append('opacity', '1');
        fd.append('visibleByDefault', 'true');
        fd.append('sortOrder', '0');
        if (user) fd.append('createdBy', user.id);
        await pb.collection('base_layers').create(fd);
      }
    }
    if (heights && payload.heightmap) {
      const fd = new FormData();
      fd.append('heightmap', heights);
      fd.append('heightmapMeta', JSON.stringify(payload.heightmap));
      await pb.collection('maps').update(map.id, fd);
    }
  };

  const wrongWorld = payload && map && payload.world?.worldId !== map.worldId;

  return (
    <div className="admin-import">
      <p className="muted small">{t('admin.import.help')}</p>
      <label>
        unity-export.json
        <input
          type="file"
          accept="application/json,.json"
          onChange={(e) => void readJson(e.target.files?.[0])}
        />
      </label>
      <label>
        {t('admin.import.render')} (topdown.jpg / .png)
        <input
          type="file"
          accept="image/jpeg,image/png"
          onChange={(e) => setRender(e.target.files?.[0] ?? null)}
        />
      </label>
      <label>
        {t('admin.import.heights')} (heights.f32.bin)
        <input type="file" accept=".bin" onChange={(e) => setHeights(e.target.files?.[0] ?? null)} />
      </label>
      {payload && (
        <p className="small">
          {payload.world?.worldId} · {payload.exportedAt} · {payload.git?.commit?.slice(0, 8)}
          {payload.git?.dirty ? ` (${t('admin.import.dirty')})` : ''}
        </p>
      )}
      {wrongWorld && (
        <div className="error">
          {t('admin.import.wrongWorld', { got: payload.world.worldId, want: map.worldId })}
        </div>
      )}
      <label className="check-row">
        <input type="checkbox" checked={fastForward} onChange={() => setFastForward(!fastForward)} />
        {t('admin.import.fastForward')}
      </label>
      <div className="row">
        <button disabled={!payload || busy || !!wrongWorld} onClick={() => void run(true)}>
          {t('admin.import.dryRun')}
        </button>
        <button
          className="primary"
          disabled={!payload || busy || !!wrongWorld || !plan?.dryRun}
          onClick={() => void run(false)}
        >
          {t('admin.import.apply')}
        </button>
      </div>
      {plan && <PlanView plan={plan} />}
    </div>
  );
}

function PlanView({ plan }: { plan: ImportPlan }) {
  const { t } = useTranslation();
  return (
    <div className="plan">
      <h4>{plan.dryRun ? t('admin.import.planTitle') : t('admin.import.resultTitle')}</h4>
      <dl className="stats">
        {Object.entries(plan.counts).map(([k, v]) => (
          <div key={k} className="stat-pair">
            <dt>{t(`admin.import.counts.${k}`)}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      {plan.items.length > 0 && (
        <table className="vertices small">
          <tbody>
            {plan.items.map((it) => (
              <tr key={it.unityKey}>
                <td>
                  <span className={`sync-pill ${it.action}`}>
                    {t(`admin.import.actions.${it.action}`, { defaultValue: it.action })}
                  </span>
                </td>
                <td>{t(`types.${it.type}`, { defaultValue: it.type })}</td>
                <td className="ellipsis">{it.name}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {plan.warnings.length > 0 && (
        <details>
          <summary>
            {t('admin.import.warnings')} ({plan.warnings.length})
          </summary>
          <ul className="small">
            {plan.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
